/**
 * Dialog for creating a new Reward Catalog Item.
 *
 * Supports 3 reward types: coupon, free_item, custom.
 * Calls admin_api.create_reward on submit.
 *
 * Plan 177, Epic 02 — Admin Setup Wizard
 */

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useCreateReward } from "../api/rewardsAdminHooks";

interface AddRewardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddRewardDialog({ open, onOpenChange }: AddRewardDialogProps) {
  const [title, setTitle] = useState("");
  const [rewardType, setRewardType] = useState("coupon");
  const [pointsCost, setPointsCost] = useState(500);
  const [description, setDescription] = useState("");

  const createMutation = useCreateReward();

  const resetForm = () => {
    setTitle("");
    setRewardType("coupon");
    setPointsCost(500);
    setDescription("");
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error("กรุณาตั้งชื่อรางวัล");
      return;
    }
    if (pointsCost <= 0) {
      toast.error("แต้มที่ใช้แลกต้องมากกว่า 0");
      return;
    }

    createMutation.mutate(
      {
        title: title.trim(),
        reward_type: rewardType,
        points_cost: pointsCost,
        description: description.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success("เพิ่มรางวัลสำเร็จ");
          resetForm();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message || "เกิดข้อผิดพลาด กรุณาลองอีกครั้ง");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>เพิ่มรางวัล</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Reward type */}
          <div className="space-y-2">
            <Label>ประเภทรางวัล</Label>
            <Select value={rewardType} onValueChange={setRewardType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="coupon">คูปอง</SelectItem>
                <SelectItem value="free_item">ของแถม</SelectItem>
                <SelectItem value="custom">กำหนดเอง</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="reward-title">ชื่อรางวัล</Label>
            <Input
              id="reward-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="เช่น ส่งฟรี, ส่วนลด ฿100"
            />
          </div>

          {/* Points cost */}
          <div className="space-y-2">
            <Label htmlFor="reward-points">แต้มที่ใช้แลก</Label>
            <div className="flex items-center gap-2">
              <Input
                id="reward-points"
                type="number"
                min={1}
                step={1}
                value={pointsCost}
                onChange={(e) => setPointsCost(Number(e.target.value))}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">แต้ม</span>
            </div>
          </div>

          {/* Description (optional) */}
          <div className="space-y-2">
            <Label htmlFor="reward-desc">
              รายละเอียด{" "}
              <span className="text-muted-foreground font-normal">
                (ไม่จำเป็น)
              </span>
            </Label>
            <Input
              id="reward-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="รายละเอียดเพิ่มเติมสำหรับลูกค้า"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="min-h-[44px]"
          >
            {createMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            เพิ่มรางวัล
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
